import { env } from '../../config';
import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';

import { getBasePlaylists, savePlaylist, updatePlaylistVersion, isProductPurchased, syncWithMonolith, syncRelatedPlaylists } from './utils';
import { formatDate, formatDateMonthFirst, getCustomers } from '../../utils';
import constants from './constants.json';
import { ReactSVG } from 'react-svg';
import ScreenConfiguration from './ScreenConfiguration';
import TransportationContent from './TransportationContent';
import NearbyConfiguration from './NearbyConfiguration';
import BrandingConfiguration from './BrandingConfiguration';
import InteractivityConfiguration from './InteractivityConfiguration';
import PreviewModal from './PreviewModal';
import screenFeatures from './screenFeatures.json';
import dropdownOptions from './dropdownOptions.json';

import buildingIcon from '../../images/icons/screens/building.svg';
import coffeeIcon from '../../images/icons/screens/coffee.svg';
import copyIcon from '../../images/icons/screens/copy.svg';
import eyeIcon from '../../images/icons/screens/eye.svg';
import gearIcon from '../../images/icons/screens/gear.svg';
import globeIcon from '../../images/icons/screens/globe.svg';
import keyIcon from '../../images/icons/screens/key.svg';
import mobileIcon from '../../images/icons/screens/mobile.svg';
import openReport from '../../images/icons/analytics/open-report.svg';
import paperPlaneIcon from '../../images/icons/screens/paper-plane.svg';
import pauseIcon from '../../images/icons/screens/pause.svg';
import playlistIcon from '../../images/icons/screens/playlist.svg';
import plusIcon from '../../images/icons/screens/white-plus.svg';
import portraitIcon from '../../images/icons/screens/portrait-left.svg';
import returnIcon from '../../images/icons/screens/return.svg';
import saveIcon from '../../images/icons/screens/save.svg';
import screenIcon from '../../images/icons/screens/screen-header.svg';
import transitIcon from '../../images/icons/screens/transit.svg';
import tripPlannerIcon from '../../images/icons/screens/trip-planner.svg';
import paintbrushIcon from '../../images/icons/screens/paintbrush.svg';
import interactivityIcon from '../../images/icons/screens/interactivity.svg';
import weatherIcon from '../../images/icons/screens/weather.svg';

const icons = {
	gearIcon,
	transitIcon,
	coffeeIcon,
	buildingIcon,
	paperPlaneIcon,
	tripPlannerIcon,
	paintbrushIcon,
	interactivityIcon,
	weatherIcon
};

const ScreenPage = ({
	lang,
	userData,
	access,
	customer,
	permissions,
	locations,
	handleContentModalOpen,
}) => {

    const [state, setState] = useState({
        playlist: {
			language: 'en',
			rotation: 0,
			interactivityConfigs: {
				
			}
		},
		transportationConfigs: constants.DEFAULT_TRANSPORTATION_CONFIGS
    })
	const [savedState, setSavedState] = useState({
        playlist: {
			language: 'en',
			rotation: 0
		},
		transportationConfigs: constants.DEFAULT_TRANSPORTATION_CONFIGS
    })
	const [stateChanged, setStateChanged] = useState(false);
    const [selectedTab, setSelectedTab] = useState('screenConfig');
	const [playlists, setPlaylists] = useState({});
	const [showNewScreenModal, setShowNewScreenModal] = useState(false);
	const [showAdminCreateModal, setShowAdminCreateModal] = useState(false);
	const [showOpenScreenModal, setShowOpenScreenModal] = useState(false);
	const [playlistsLoading, setPlaylistsLoading] = useState(true);
	const [showLicenseModal, setShowLicenseModal] = useState(false);
	const [statusMessage, setStatusMessage] = useState({
		message: null,
		error: false
	});
	const [showUndoConfirmModal, setShowUndoConfirmModal] = useState(false);
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

	const [showPreviewModal, setShowPreviewModal] = useState(false);

	const [showDeleted, setShowDeleted] = useState(false);

	// Must also check for location purchase
	
	const purchasedProducts = state?.playlist?.location?.purchasedProducts || {};

	const disableIcons = !state?.playlist?.createdAt ||
		!state?.playlist?.isEditor ||
		// Check if the location that the playlist changed to has the product purchased
		!isProductPurchased(state?.playlist?.screenType, purchasedProducts)

	const getPlaylists = async () => {
		let basePlaylists = await getBasePlaylists(customer.id, customer.id === userData.customer_id && userData.role !== 'af_admin' ? userData.sub : null, null, 'journey');
		if (userData.role === 'af_admin') {
			basePlaylists.forEach((playlist, index) => {
				basePlaylists[index].isEditor = true;
			});
		}

		return basePlaylists;
	}

	useEffect(() => {
		getPlaylists()
		.then(data => {
			setPlaylists(data)
			setPlaylistsLoading(false)
			//If only one playlist, then set that one playlist as the selected one from the start
			if (data.length === 1) {
				const playlist = data[0];
				let transportationConfigs = {...constants.DEFAULT_TRANSPORTATION_CONFIGS};

				if (playlist?.url) {
					const urlObject = new URLSearchParams(playlist?.url.split('?')[1]);
					// Update current state to BIZ values
					for (const [key, value] of urlObject.entries()) {
						//Set boolean types first
						if (value === 'true' ) transportationConfigs[key] = true;
						else if (value === 'false' ) transportationConfigs[key] = false;
						else transportationConfigs[key] = value;
					}
				}

				// Initial saving of state
				setState({
					playlist: playlist,
					transportationConfigs: transportationConfigs
				});
			}
		});
	}, [showDeleted])

	const oneViewOptions = useMemo(() => {
		if (!customer) {
			return []; // Return an empty array if customer is not available
		}

		let options = [];
		let disabledOptions = ["directory", "wayfinding", "weather"];
		dropdownOptions.VIEWS.forEach((option) => {
			if (state.playlist?.location) {
				// The user's permissions determine if they have the option
				option.isDisabled = !(option.requirement in permissions) // Option requirement is not present
					// Option requirement is present, but no permission for location
					|| !(state?.playlist?.location?.id in permissions[option.requirement])
				options.push(option);
			} else if (state.playlist?.createdAt) {
				// No location, possibly creating a screen right now as af_admin
				options.push(option);
			}
	    });

	    return options;
	}, [state.playlist]);

	const handleInputChange = (property, value) => {
		const tempPlaylist = {...state.playlist};
        tempPlaylist[property] = value;
        // Update the location object, too.
        if (property === "locationId") {
            tempPlaylist["location"] = locations.find((location) => location.id === value);
        }
        setState({
            ...state,
            playlist: tempPlaylist
        });
		setStateChanged(true);
    }

	//For transportation configs
	const handleConfigChanges = (propNames, values) => {
		const tempConfigs = {...state.transportationConfigs};
		propNames.forEach((item, index) => {
			tempConfigs[item] = values[index];
		});

		setState({
			...state,
			transportationConfigs: tempConfigs
		});
		setStateChanged(true);
	}

	const handleMultiInputChanges = (propNames, values) => {
		const tempPlaylist = {...state.playlist};        
		
		propNames.forEach((item, index) => {
		  tempPlaylist[item] = values[index];
		});

		setState({
            ...state,
            playlist: tempPlaylist
        });
		setStateChanged(true);
	}

	const handlePlaylistDelete = async () => {
		const tempState = {
			...state
		};
		tempState.playlist.isDeleted = true;

		await savePlaylist(tempState, customer)
		.then(data => {
			//Fetch playlists again with update
			if (data.status === 200) {
				setStatusMessage({
					message: 'Your playlist has been deleted.',
					error: false
				});
				setStateChanged(false);
				getPlaylists()
				.then(data => {
					//Set back to default
					setState({
						playlist: {
							language: 'en',
							rotation: 0,
							interactivityConfigs: {
								
							}
						},
						transportationConfigs: constants.DEFAULT_TRANSPORTATION_CONFIGS
					})
					setPlaylists(data);
				});
			}
			//Failed save
			else {
				setStatusMessage({
					message: 'Error saving, please try again.',
					error: true
				});
			}
		});

	}

	const saveScreen = async () => {
		if (state.playlist.screenType === 'contentPlaylist' && !state.playlist.contentShortCode) {
			setStatusMessage({
				message: 'No playlist specified',
				error: true
			})
			return;
		}
		let saveResp = await savePlaylist(state, customer);
		if (state.playlist.tsDeviceId) {
			await syncWithMonolith(state);
			// If there are related playlists, sync them first before fetching all of the
			// playlists again.
			await syncRelatedPlaylists(state);
		}
		if (saveResp.status === 200) {
			//Fetch playlists again with update
			setStatusMessage({
				message: 'Your updates have been saved.',
				error: false
			});
			setStateChanged(false);
			getPlaylists().then(data => {
				setState({
					...state,
					//Update the current playlist in case BIZ gives more details
					//Finding based on label and customerId bc this may be a new playlsit that was just created
					playlist: data.find(pl => 'id' in state.playlist
						? pl.id === state.playlist.id
						: pl.label === state.playlist.label && pl.customerId === state.playlist.customerId
					),
				})
				setPlaylists(data);
			});
		} else {
			//Failed save
			setStatusMessage({
				message: 'Error saving, please try again.',
				error: true
			});
		}
	}

	const createScreen = (customerId, location) => {
		let defaultScreenType = 'standby';
		// Default is all modes allowed
		let transportationConfigs = {...constants.DEFAULT_TRANSPORTATION_CONFIGS};
		setState({
			playlist: {
				customerId: customerId,
				locationId: location.id,
				location,
				geolocation: false,
				isEditor: true,
				label: 'No name provided',
				language: 'en',
				locationType: 'coordinates',
				measurement: 'imperial',
				screenType: defaultScreenType,
				deviceType: 'landscape',
				createdAt: new Date(),
				_new: true
			},
			transportationConfigs: transportationConfigs
		})
	}

	const handleScreenSelection = (playlist) => {
		let brandingConfigs = {...constants.DEFAULT_BRANDING_CONFIGS, ...playlist.brandingConfigs};
		let interactivityConfigs = {...constants.DEFAULT_INTERACTIVITY_CONFIGS, ...playlist.interactivityConfigs};
		let transportationConfigs = {
			...constants.DEFAULT_TRANSPORTATION_CONFIGS,
			...constants.TRANSPORTATION_CONFIG_OVERRIDES[playlist.product],
			...playlist.transportationConfigs
		};
		let nearbyConfigs = {...constants.DEFAULT_NEARBY_CONFIGS, ...playlist.nearbyConfigs};

		// Initial saving of state
		setState({
			playlist: {
				...playlist,
				language: playlist?.language ?? 'en', //For the legacy screens that did not have langauges preset,
				brandingConfigs,
				interactivityConfigs,
				transportationConfigs,
				nearbyConfigs
			}
		});
		setSavedState({
			playlist: {
				...playlist,
				language: playlist?.language ?? 'en',
				brandingConfigs: brandingConfigs,
				interactivityConfigs: interactivityConfigs
			}
		})
		setStateChanged(false);

		setShowOpenScreenModal(false);
	}

	const userHasAccess = (featureKey) => {
		// The Basic Configuration tab is accessible by default if user has perms for screen editing.
		const defaultAllowed = ['screenConfig'].includes(featureKey);
		let viewableFeature = featureKey in permissions && (state?.playlist?.location?.id in permissions[featureKey]);

		return defaultAllowed || viewableFeature;
	}

    const getUserAccessClassNames = (feature) => {
    	var retval = '';
    	// No access means the tab can't be selected.
		if (!userHasAccess(feature.key) || !feature.enabled || !state?.playlist?.createdAt) {
    		retval += 'disabled-icon-container';
    	} else if (selectedTab === feature.key) {
    		retval += 'selected-tab';
    	}

    	return retval;
    }

	const fillSvgColor = (svg, stroke, fill) => {
		if (stroke || fill) {
			for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', stroke);
				svg.children[i].setAttribute('fill', fill);
            }
		}
	}

	const determineToggle = (key) => {
		if (key === 'tripplanner') {
			//See if tripPlan is set to true, then turn on toggle
			return state.transportationConfigs?.tripPlan ? state.transportationConfigs?.tripPlan : false;
		}
	}

    const modifyIconColor = (svg, feature) => {
        const needsFill = ['coffeeIcon', 'interactivityIcon', 'weatherIcon', 'paintbrushIcon'];
        if (!userHasAccess(feature.key) || !feature.enabled || !state?.playlist?.createdAt) {
        	// Gray if no access
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', '#CCD2D3');
                if (needsFill.includes(feature.icon)) {
                	svg.children[i].setAttribute('fill', '#CCD2D3');
                }
            }
        } else if (selectedTab == feature.key) {
        	for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', 'white');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', 'white');
                }
            }
        }
		//Some icons are only for toggle, and won't change the page displayed
		else if (determineToggle(feature.key)) {
        	for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', 'white');
            }
        } else if (userHasAccess(feature.key) && feature.enabled && state?.playlist?.createdAt) {
            // Explicitly turn it the "active" color
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', '#373739');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', '#373739');
                }
            }
        }
    }

	const handleFeatureToggle = (key) => {
		//Set the toggle value to a new one, or if the same icon has been clicked, turn toggle off		
		if (key === 'tripplanner') {
			//Turn tripPlan on or off depending on toggle
			handleConfigChanges(['tripPlan'], state.transportationConfigs?.tripPlan !== null ? [!state.transportationConfigs?.tripPlan] : [false]);
		}	
	}

    const getMenuItem = (feature) => {
        if (feature.endGrouping) {
            menu.push(<div className="vertical-line" key={feature.key}></div>);
            return;
		} else if (feature.module.includes(access)) {
			//First check for access per feature item
			return <div key={feature.key} title={feature.title}>
				<div
					className={`icon-container ${getUserAccessClassNames(feature)} ${determineToggle(feature.key) ? "selected-feature" : ""}`} 
					title={feature.title}
					//The tab can either have a full page to edit, or be a toggle feature to turn on/off
					onClick={() => feature.hasPage ? setSelectedTab(feature.key) : handleFeatureToggle(feature.key)
				}>
					<ReactSVG className="icon feature-icon" src={icons[feature.icon]} beforeInjection={(svg) => modifyIconColor(svg, feature)}/>
				</div>
			</div>
		}
    }

	const undoChange = () => {
		setState(savedState);
		setStateChanged(false);
	}

    let menu = [];
    screenFeatures.forEach((feature) => {
		menu.push(getMenuItem(feature));
	})

	//Different modals we have for this page
	const renderModal = () => {
		if (showNewScreenModal) return <NewScreenModal setShowNewScreenModal={setShowNewScreenModal} setShowAdminCreateModal={setShowAdminCreateModal} isAdmin={userData.role === 'af_admin'} customerId={customer.id} createScreen={createScreen} />;
		if (showAdminCreateModal) return <AdminCreateModal setShowAdminCreateModal={setShowAdminCreateModal} getCustomers={getCustomers} setState={setState} createScreen={createScreen} locations={locations} customerId={customer.id} />;
		if (showOpenScreenModal) return <OpenScreenModal playlists={playlists} selectedPlaylist={state?.playlist} handleScreenSelection={handleScreenSelection} setShowOpenScreenModal={setShowOpenScreenModal} showDeleted={showDeleted} setShowDeleted={setShowDeleted} userData={userData} />;
		if (showLicenseModal) return <LicenseModal state={state} setShowLicenseModal={setShowLicenseModal} handleInputChange={handleInputChange} handleMultiInputChanges={handleMultiInputChanges} saveScreen={saveScreen} setShowDeleteConfirmModal={setShowDeleteConfirmModal} customer={customer} stateChanged={stateChanged} user={userData} setState={setState} savePlaylist={savePlaylist} setStatusMessage={setStatusMessage} setStateChanged={setStateChanged} getPlaylists={getPlaylists} setPlaylists={setPlaylists} />;
		if (showUndoConfirmModal) return <UndoConfirmModal setShowUndoConfirmModal={setShowUndoConfirmModal} undoChange={undoChange} />;
		if (showDeleteConfirmModal) return <DeleteConfirmModal label={state.playlist.label} setShowDeleteConfirmModal={setShowDeleteConfirmModal} handlePlaylistDelete={handlePlaylistDelete} />;
		if (statusMessage.message) return <StatusModal setStatusMessage={setStatusMessage} statusMessage={statusMessage} />;
		if (showPreviewModal) return <PreviewModal state={state} setShowPreviewModal={setShowPreviewModal} />;
		return null;
	};

	return (
		<div className="ScreenPage">
			{renderModal()}
            <div className="screen-actions">
                <div className="buttons-and-name">
                    <div className="buttons-container pure-u-1-9">
                        <button className="open-screen" onClick={() => setShowNewScreenModal(true)}>
                            <img className="open-icon" src={plusIcon}/>
                        </button>
                        <button className="open-screen" title="Open a Screen or Mobile" onClick={() => setShowOpenScreenModal(true)} disabled={playlistsLoading}>
                            <img className="open-icon" src={openReport}/>
                        </button>
                    </div>
                    <div className="name-container pure-u-1-7">
                        <div className="name">
                            { state?.playlist?.label ? state.playlist.label : "No screen selected" }
                        </div>
                        {
                            state?.playlist?.shortCode
                            ? <div className="activation-code-description">Activation code: <span className="screen-activation-code">{state.playlist.shortCode.toUpperCase()}</span></div>
                            : <div className="open-a-screen">Open a Screen to view configurations</div>
                        }
                    </div>
				</div>
				<div className="screen-tabs">
					{menu}
					<div className="vertical-line"></div>
					<div title="Preview Screen">
						<div className={`icon-container ${!state.playlist?.createdAt || stateChanged ? "disabled" : ""}`} title="Preview screen" onClick={() => !state.playlist?.createdAt || stateChanged ? null : setShowPreviewModal(true)}>
							<ReactSVG className="icon preview-screen-icon" src={eyeIcon} beforeInjection={(svg) => !state.playlist?.createdAt || stateChanged ? fillSvgColor(svg, '#CCD2D3', '#CCD2D3') : null}/>
						</div>
					</div>
                    <div title="Undo all changes">
						<div className={`icon-container ${disableIcons ? "disabled" : ""}`} title="Undo all changes" onClick={() => setShowUndoConfirmModal(true)}>
							<ReactSVG className="icon" src={returnIcon} beforeInjection={(svg) => disableIcons ? fillSvgColor(svg, '#CCD2D3') : null}/>
						</div>
					</div>
                    <div title="Save changes" className={`icon-container  ${disableIcons ? "disabled" : ""} ${stateChanged ? "changed" : ""}`} onClick={disableIcons? null : saveScreen}>
						<ReactSVG className="icon" src={saveIcon} beforeInjection={(svg) => disableIcons ? fillSvgColor(svg, null, '#CCD2D3') : stateChanged ? fillSvgColor(svg, null, '#fff') : null} />
					</div>
					<div title="Manage license" className={`icon-container ${!state?.playlist?.createdAt ? "disabled" : "manage-license-button"}`} onClick={() => setShowLicenseModal(state.playlist?.createdAt ? true: false)}>
						<ReactSVG className="icon" src={keyIcon} beforeInjection={(svg) => !state?.playlist?.createdAt ? fillSvgColor(svg, '#CCD2D3', null) : null} />
					</div>
				</div>
			</div>
			{
				selectedTab === 'screenConfig'
				? <ScreenConfiguration
					state={state}
					setState={setState}
					handleInputChange={handleInputChange}
					handleMultiInputChanges={handleMultiInputChanges}
					customerId={customer.id}
					selectedProduct={state?.playlist?.product}
					locations={locations}
					oneViewOptions={oneViewOptions}
					allPermissions={permissions}
					user={userData}
				/>
				:
				selectedTab == 'transportationConfig' && userHasAccess('transportationConfig')
				? <TransportationContent
					state={state}
					setState={setState}
					setStateChanged={setStateChanged}
					permissions={permissions}
					user={userData}
				/>
				: selectedTab == 'nearby' && userHasAccess('nearby')
				? <NearbyConfiguration
					state={state}
					setState={setState}
					setStateChanged={setStateChanged}
					permissions={permissions}
					user={userData}
				/>
				: selectedTab == 'brandingConfiguration'
				? <BrandingConfiguration
					state={state}
					setState={setState}
					setStateChanged={setStateChanged}
					oneViewOptions={oneViewOptions}
					permissions={permissions}
					purchasedProducts={state.playlist?.location?.purchasedProducts}
					apiKey={customer.apiKey}
					handleContentModalOpen={handleContentModalOpen}
					user={userData}
				/>
				: selectedTab === 'interactivityConfiguration'
				? <InteractivityConfiguration
					state={state}
					setState={setState}
					setStateChanged={setStateChanged}
					permissions={permissions}
					user={userData}
				/>
				: <ScreenConfiguration
					state={state}
					setState={setState}
					handleInputChange={handleInputChange}
					handleMultiInputChanges={handleMultiInputChanges}
					customerId={userData.customer_id}
					selectedProduct={state?.playlist?.product}
					customer={customer}
					locations={locations}
					oneViewOptions={oneViewOptions}
					allPermissions={permissions}
					user={userData}
				/>
			}
        </div>
	)
}

const NewScreenModal = ({ setShowNewScreenModal, setShowAdminCreateModal, isAdmin }) => {

	useEffect(() => {
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};
	}, []);
	
	const handleEmailRequest = () => {
		window.open('mailto:support@actionfigure.ai?subject=New Actionfigure Screen inquiry')
		setShowNewScreenModal(false);
	}

	//Open Admin Create modal
	const handleAdminClick = () => {
		setShowNewScreenModal(false);
		setShowAdminCreateModal(true);
	}

    return (
		<div className="modal-wrapper">
			<div className="NewScreenModal modal">
				<div className="modal-header">
					<h2>Create a new Actionfigure Screen</h2>
				</div>
				<p>Ready to create a new Actionfigure Screen? Our team is here to help!</p>
				<p>Simply click the “Contact Support” button below and drop us an email at <a href="mailto:support@actionfigure.ai">support@actionfigure.ai</a> to let us know you are interested in adding a new Screen!</p>
				<div className="bottom-container">
					{
						isAdmin
						? <button className="button admin-button" onClick={handleAdminClick}>Admin Only - Create New</button>
						: <></>
					}
					<button className="button cancel-button" onClick={() => setShowNewScreenModal(false)}>Cancel</button>
					<button className="button modal-button" onClick={handleEmailRequest}>Contact Support</button>
				</div>	
			</div>
		</div>
    )
}

const AdminCreateModal = ({ setShowAdminCreateModal, getCustomers, setState, createScreen, locations, customerId }) => {

	const [customers, setCustomers] = useState([]);
	const [selectedCustomerId, setSelectedCustomerId] = useState(env.ACTIONFIGURE_CUSTOMER_ID);
	const [selectedLocationId, setSelectedLocationId] = useState();
	const locationOptions = locations.map(location => {
		return {
			label: location.name,
			value: location.id
		}
	})
	const [error, setError] = useState();

	const createCustomerOptions = async () => {
		await getCustomers()
		.then(data => {
			const customerOptions = data.map(customer => {
				//Skip the Actionfigure option as it exists as a separate radio button option
				if (customer.id === env.ACTIONFIGURE_CUSTOMER_ID) {
					return;
				}
				return {
					value: customer.id,
					label: customer.name
				}
			}).filter(n=>n)
			
			setCustomers(customerOptions.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())));
		})
	}
	
	useEffect(() => {
		createCustomerOptions();
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};		
	}, [])

	const handleCreate = () => {
		const selectedLocation = locations.find(location => location.id === selectedLocationId);
		createScreen(customerId, selectedLocation);
		setShowAdminCreateModal(false);
	}

    return (
		<div className="modal-wrapper">
			<div className="AdminCreateModal modal">
				<div className="modal-header">
					<h2>Choose a customer</h2>				
				</div>
				<p>Which customer should have access to this Screen?</p>
				<div className="customer-options">
					<div className="radio-container disabled-section">
						<input type="radio" className="radio-input" checked={selectedCustomerId !== env.ACTIONFIGURE_CUSTOMER_ID} readOnly disabled={true} />
						<Select  
							onChange={(option) => setSelectedCustomerId(option.value)}
							options={customers}
							value={ customers.find(customer => customer.value === selectedCustomerId) }
							className={"dropdown"}
							styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
							isDisabled={true}
						/>
					</div>
					<div className="radio-container">
						<input type="radio" className="radio-input" onChange={() => setSelectedCustomerId(env.ACTIONFIGURE_CUSTOMER_ID)} checked={selectedCustomerId === env.ACTIONFIGURE_CUSTOMER_ID} />
						<p className="text">Actionfigure only</p>
					</div>
					<div className="input-container">
						<div className="basic-settings__label">Location:</div>
						<div className="basic-settings__value">
							<Select
								onChange={(option) => setSelectedLocationId(option.value)}
								options={locationOptions}
								value={ locations.find(location => location.value === selectedLocationId) }
								className={"switch-field"}
							/>
						</div>
					</div>
					<p style={{color: 'red'}}>{error}</p>
				</div>
				<div className="bottom-container">
					<button className="button cancel-button" onClick={() => setShowAdminCreateModal(false)}>Cancel</button>
					<button className="button admin-button" onClick={selectedLocationId ? handleCreate : () => setError('Please select a location first')}>Create</button>			
				</div>
			</div>
		</div>
    )
}

const OpenScreenModal = ({ playlists, selectedPlaylist, handleScreenSelection, setShowOpenScreenModal, showDeleted, setShowDeleted, userData }) => {

	useEffect(() => {
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};
	}, []);

	const [highlightPlaylist, setHighlightPlaylist] = useState(selectedPlaylist);
	const [sortColumn, setSortColumn] = useState('updatedAt');
    const [sortDirection, setSortDirection] = useState('desc');


	const handleRowClick = (pl) => () => setHighlightPlaylist(pl.url ? pl : null);

	const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

	const sortedPlaylists = [...playlists].sort((a, b) => {
    let aValue, bValue;

    if (sortColumn === 'locationId') {
        aValue = a.location?.name || '';
        bValue = b.location?.name || '';
    } else if (sortColumn === 'city') {
        aValue = a.location?.city || '';
        bValue = b.location?.city || '';
    } else if (sortColumn === 'state') {
        aValue = a.location?.state || '';
        bValue = b.location?.state || '';
    } else {
        aValue = a[sortColumn];
        bValue = b[sortColumn];
    }

    // Handle null/undefined
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Date columns
    if (['updatedAt', 'activationDate', 'deactivationDate'].includes(sortColumn)) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
    }

    // String comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (sortDirection === 'asc') {
            return aValue.localeCompare(bValue);
        } else {
            return bValue.localeCompare(aValue);
        }
    }

    // Number or Date comparison
    if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
});

	const playlistRows = playlists.length === 0
	? <></>
	: sortedPlaylists
		.filter(pl => showDeleted || !pl.isDeleted)
		.map(pl => {
			return <tr key={pl?.id} className={`playlist-row ${pl?.id === highlightPlaylist?.id ? 'selected' : ''}`} onClick={handleRowClick(pl)}>
				<td>{pl?.shortCode}</td>
				<td>{
						pl?.label && pl?.label.length > 40
						? pl.label.slice(0, 40) + '…'
						: pl?.label
					}
				</td>
				<td>
					{
						pl?.location?.name && pl.location.name.length > 40
						? pl.location.name.slice(0, 40) + '…'
						: pl?.location?.name
					}
				</td>
				<td> { pl?.location?.city }</td>
				<td> { pl?.location?.state }</td>
				{
					pl?.deviceType === 'mobile'
					? <td><img src={mobileIcon} alt="copy" className="screen-icon"/>{pl?.deviceType || 'mobile'}</td>
					: pl?.deviceType === 'landscape'
					? <td><img src={screenIcon} alt="copy" className="screen-icon"/>{pl?.deviceType || 'screen'}</td>
					: <td><img src={portraitIcon} alt="copy" className="screen-icon"/>{pl?.deviceType || 'portrait'}</td>
				}
				{
					pl?.isDeleted
					? <td className="column-deactivated">Deactivated</td>
					: pl?.screenType === 'transportation'
					? <td><img src={transitIcon} alt="copy" className="screen-type-icon"/>{pl?.screenType}</td>
					: pl?.screenType === 'nearby'
					? <td><img src={coffeeIcon} alt="copy" className="screen-type-icon"/>{pl?.screenType}</td>
					: pl?.screenType === 'contentPlaylist'
					? <td><img src={playlistIcon} alt="copy" className="screen-type-icon"/>{pl?.screenType}</td>
					: pl?.screenType === 'other'
					? <td><img src={globeIcon} alt="copy" className="screen-type-icon"/>{pl?.screenType}</td>
					: <td><img src={pauseIcon} alt="copy" className="screen-type-icon"/>Standby</td>
				}
				<td>{formatDate(pl?.updatedAt)}</td>
			</tr>
		});

	const screenSelection = () => {
		if (!highlightPlaylist) return; // Prevents errors if no playlist is selected
		handleScreenSelection(highlightPlaylist);
	}

	const closeOpenScreenModal = () => {
		setShowOpenScreenModal(false);
	}

    return (
		<div className="modal-wrapper">
			<div className="OpenScreenModal modal">
				<div className="modal-header">
					<h2>Open Actionfigure® Screen or Mobile</h2>				
				</div>
				<div className="table-container">
					<table className="screens-table">
						<tbody>
							<tr className="header-row">
                                <th className="table-header" onClick={() => handleSort('shortCode')}>Code</th>
                                <th className="table-header" onClick={() => handleSort('label')}>Name</th>
                                <th className="table-header" onClick={() => handleSort('locationId')}>Location</th>
                                <th className="table-header" onClick={() => handleSort('city')}>City</th>
                                <th className="table-header" onClick={() => handleSort('state')}>State</th>
                                <th className="table-header" style={{cursor: "default"}}>Device type</th>
                                <th className="table-header" onClick={() => handleSort('screenType')}>What's playing</th>
                                <th className="table-header" onClick={() => handleSort('updatedAt')}>Updated</th>
                            </tr>
							{playlistRows}
						</tbody>
					</table>
				</div>
				<div className="bottom-container">
					{ userData.role === "af_admin"
						? <div className="optional">
							<input checked={showDeleted} name="showDeleted" onChange={(e) => setShowDeleted(e.target.checked)} type="checkbox" />
							<label className="include-deleted">Show deactivated screens</label>
						</div>
						: <></>
					}
					<div className="submit-buttons-container" style={ userData.role !== "af_admin" ? {width: "100%"} : {}}>
						<button className="button cancel-button" onClick={closeOpenScreenModal}>Cancel</button>
						<button className="button modal-button" onClick={screenSelection}>Open</button>
					</div>
				</div>
			</div>
		</div>
    )
}

const LicenseModal = ({ state, setShowLicenseModal, handleInputChange, handleMultiInputChanges, saveScreen, setShowDeleteConfirmModal, customer, stateChanged, user, setState, savePlaylist, setStatusMessage, setStateChanged, getPlaylists, setPlaylists }) => {

	useEffect(() => {
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};
	}, []);

	const devices = state?.playlist?.devices;
	
	const handleSubmit = async() => {
		setShowLicenseModal(false);

		if (!stateChanged) return; //Just return if nothing was changed

		await saveScreen();
	}

	const handleRefresh = async () => {
		try {
			const resp = await updatePlaylistVersion(state?.playlist?.id);
			if (resp.status === 201) {
				console.log("Success");
			} else {
				console.error("Version update failed");
			}
		} catch (error) {
			console.error("Error updating playlist version:", error);
		} finally {
			setShowLicenseModal(false);
		}
	}

	const handleDelete = async() => {
		setShowLicenseModal(false);
		setShowDeleteConfirmModal(true)
	}

	const handleActivateScreen = async() => {
		const tempState = {
			...state
		};
		tempState.playlist.isDeleted = false;

		await savePlaylist(tempState, customer)
		.then(data => {
			//Fetch playlists again with update
			if (data.status === 200) {
				setStatusMessage({
					message: 'Your playlist has been reactivated.',
					error: false
				});
				setStateChanged(false);
				setShowLicenseModal(false);
				getPlaylists()
				.then(data => {
					//Set back to default
					setState({
						playlist: {
							language: 'en',
							rotation: 0,
							interactivityConfigs: {}
						},
						transportationConfigs: constants.DEFAULT_TRANSPORTATION_CONFIGS
					})
					setPlaylists(data);
				});
			}
			//Failed save
			else {
				setStatusMessage({
					message: 'Error saving, please try again.',
					error: true
				});
			}
		});
	}

	//Copies text to clipboard
	const copyText = (value) => {
		if (!value) return;

		navigator.clipboard.writeText(value);
	}

	const getManageLicense = () => {
		if (user.role === 'af_admin') {
			return <>
				<div className="modal-header">
					<h2>Manage license</h2>
				</div>
				<div className="license-container">
					<div className="left-col">
						<div className="input-container">
							<p className="label">"API" Key:</p>
							<div className="input-wrapper">
								<input className="license-input" value={customer?.apiKey || ''} disabled />
								<img src={copyIcon} alt="copy" className="input-icon" onClick={() => copyText(customer?.apiKey)}/>
							</div>
						</div>					
						<div className="input-container">
							<p className="label">Environment:</p>
							<div className="input-wrapper">
								<input className="license-input" value="Production (citymotion.io)" disabled />
							</div>
						</div>
						<div className="input-container">
							<p className="label">Activation code:</p>
							<div className="input-wrapper">
								<input className="license-input" value={state?.playlist?.shortCode} type="textbox" disabled />
								<img src={copyIcon} alt="copy" className="input-icon" onClick={() => copyText(state?.playlist?.shortCode)} />
							</div>
						</div>
						<div className="input-container">
							<p className="label">Player URL:</p>
							<div className="input-wrapper">
								<input className="license-input" value={`${env.LOADER_URL}/${state?.playlist?.shortCode}`} disabled />
								<img src={copyIcon} alt="copy" className="input-icon" onClick={() => copyText(`${env.LOADER_URL}/${state?.playlist?.shortCode}`)} />
							</div>
						</div>
						<div className="input-container">
							<p className="label">Full URL:</p>
							<div className="input-wrapper">
								<input className="license-input" value={state?.playlist?.url} disabled />
								<img src={copyIcon} alt="copy" className="input-icon" onClick={() => copyText(state?.playlist?.url)} />
							</div>
						</div>
						<div className="input-container">
							<p className="label">Type:</p>
							<div className="radio-group">
								<label className="radio-row">
									<input
										className="radio-button"
										id="location-anywhere"
										type="radio"
										name="activationType"  // Grouping the radio buttons
										value="coordinates"
										checked={state?.playlist?.locationType === 'coordinates' && !state?.playlist?.geolocation}
										onChange={() => handleMultiInputChanges(['locationType', 'geolocation', 'locationCode'], ['coordinates', false, null])}
									/>
									Anywhere (default)
								</label>
								<label className="radio-row">
									<input
										className="radio-button"
										id="location-anywhere-geolocation"
										type="radio"
										name="activationType"
										value="geolocation"
										checked={state?.playlist?.locationType === 'coordinates' && state?.playlist?.geolocation}
										onChange={() => handleMultiInputChanges(['locationType', 'geolocation', 'locationCode'], ['coordinates', true, null])}
									/>
									Anywhere with Geolocation
								</label>
								{/* Hub Option (Radio + Label + Input) */}
								<label className="radio-row hub-row">
									<input
										className="radio-button"
										id="location-hub"
										type="radio"
										name="activationType"
										value="locationCode"
										checked={state?.playlist?.locationType === 'locationCode'}
										onChange={() => handleMultiInputChanges(['locationType', 'locationCode', 'geolocation'], ['locationCode', state?.playlist?.locationCode, false])}
									/>
									<span>Hub</span>
									<input
										type="text"
										className="hub-input"
										name="locationCode"
										placeholder="Enter Location Code"
										value={state?.playlist?.locationCode || ''}
										onChange={e => handleMultiInputChanges(['locationType', 'locationCode', 'geolocation'], ['locationCode', e.target.value, false])}
									/>
								</label>
							</div>
						</div>
					</div>
					<div className="right-col">
						<div className="input-container">
							<p className="label">Enable this code:</p>
							<div className="radio-group">
								<label className="radio-row">
									<input
										className="radio-button"
										id="creation-date"
										type="radio"
										name="activationDate"  // Grouping the radio buttons
										value={state.playlist?.activationDate || ''}
										checked={state?.playlist?.activationDate === null}
										onChange={(e) => handleInputChange('activationDate', null)}
									/>
									Creation date (default)
								</label>
								<label className="radio-row">
									<input
										className="radio-button"
										id="activation-date"
										type="radio"
										name="activationDate"
										value={state.playlist?.activationDate || ''}
										checked={state?.playlist?.activationDate !== null}
										readOnly
									/>
									<input
										type="date"
										className="date-input"
										value={formatDate(state?.playlist?.activationDate) || ''}
										onChange={(e) => handleInputChange('activationDate', e.target.value)}
									/>
								</label>
							</div>
						</div>
						<div className="input-container">
							<p className="label">Disable this code:</p>
							<div className="radio-group">
								<label className="radio-row">
									<input
										className="radio-button"
										id="deactivate-never"
										type="radio"
										name="deactivationDate"  // Grouping the radio buttons
										value=""
										checked={state?.playlist?.deactivationDate === null}
										onChange={(e) => handleInputChange('deactivationDate', null)}
									/>
									Never (default)
								</label>
								<label className="radio-row">
									<input
										className="radio-button"
										id="deactivation-date"
										type="radio"
										name="deactivationDate"
										value=""
										checked={state?.playlist?.deactivationDate !== null}
										readOnly
									/>
									<input
										type="date"
										className="date-input"
										value={formatDate(state?.playlist?.deactivationDate) || ''}
										onChange={(e) => handleInputChange('deactivationDate', e.target.value)}
									/>
								</label>
							</div>
						</div>
						<div className="input-container">
							<p className="label">Activation limit:</p>
							<div className="radio-group">
								<label className="radio-row">
									<input
										className="radio-button"
										id="activationLimit-none"
										type="radio"
										name="activationLimit"  // Grouping the radio buttons
										checked={state?.playlist?.activationLimit === null}
										value=""
										onChange={(e) => handleInputChange('activationLimit', null)}
									/>
									None (default)
								</label>
								<label className="radio-row">
									<input
										className="radio-button"
										id="activation-limit"
										type="radio"
										name="activationLimit"
										checked={state?.playlist?.activationLimit !== null}
										value=""
										readOnly
									/>
									<input
										type="text"
										className="license-input"
										value={state?.playlist?.activationLimit || ''}
										onChange={(e) => handleInputChange('activationLimit', e.target.value)}
									/>
								</label>
							</div>
						</div>
						<div className="input-container">
							<p className="label"></p>
							<button className="button delete-button" onClick={state?.playlist?.isDeleted ? handleActivateScreen : handleDelete}>
								{ state?.playlist?.isDeleted ? 'Activate screen' : 'Delete screen' }
							</button>
						</div>
					</div>
				</div>
			</>
		}
	}

    return (
		<div className="modal-wrapper">
			<div className="LicenseModal modal">
				{ getManageLicense() }
				<div className="about-container">				
					<div className="left-col">
						<div className="modal-header">
							<h2>About this screen</h2>
						</div>
						<div className="input-container">
							<p className="label">Activation code:</p>
							<p className="text-value bold-text">{state?.playlist?.shortCode || ''}</p>
						</div>
						<div className="input-container">
							<p className="label">Activations:</p>
							<p className="text-value">{devices ? `${devices.length} of ${state?.playlist?.activationLimit || 'unlimited'}` : ''}</p>
						</div>
						<div className="input-container">
							<p className="label">Valid from:</p>
							<p className="text-value">{formatDateMonthFirst(state?.playlist?.activationDate) || ''}</p>
						</div>
						<div className="input-container">
							<p className="label">Valid through:</p>
							<p className="text-value">{formatDateMonthFirst(state?.playlist?.deactivationDate) || 'unlimited'}</p>
						</div>
					</div>
					<div className="right-col">
						<button className="button cancel-button">Contact Support</button>
						<button className="button refresh-button" onClick={handleRefresh}>Refresh screen</button>
						<button className="button modal-button" onClick={handleSubmit}>Close</button>
					</div>
				</div>
			</div>
		</div>
    )
}

const StatusModal = ({ setStatusMessage, statusMessage }) => {

	useEffect(() => {
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};
	}, []);

	const closeStatusModal = () => {
		setStatusMessage({message: null, error: false})
	}

    return (
		<div className="modal-wrapper">
			<div className="StatusModal modal">
				<p className={`${statusMessage.error ? "error" : ""}`}>{statusMessage.message}</p>
				<div className="bottom-container">
					<button className="button modal-button" onClick={closeStatusModal}>Ok</button>			
				</div>
			</div>
		</div>
    )
}

const UndoConfirmModal = ({ setShowUndoConfirmModal, undoChange }) => {

	useEffect(() => {
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};
	}, []);

	const handleUndoConfirm = () => {
		undoChange();
		setShowUndoConfirmModal(false);
	}

	const closeUndoConfirmModal = () => {
		setShowUndoConfirmModal(false)
	}

    return (
		<div className="modal-wrapper">
			<div className="UndoConfirmModal modal">
				<p>This will remove all changes since the last time you saved. Are you sure?</p>
				<div className="bottom-container">
					<button className="button cancel-button" onClick={closeUndoConfirmModal}>Cancel</button>
					<button className="button modal-button" onClick={handleUndoConfirm}>Ok</button>			
				</div>
			</div>
		</div>
    )
}

const DeleteConfirmModal = ({ label, setShowDeleteConfirmModal, handlePlaylistDelete }) => {

	useEffect(() => {
		// Prevent scrolling when modal is open
		document.body.classList.add("no-scroll");
	
		return () => {
			// Allow scrolling when modal is closed
			document.body.classList.remove("no-scroll");
		};
	}, []);

	const handleDeleteConfirm = async () => {
		setShowDeleteConfirmModal(false)
		await handlePlaylistDelete();
	}

	const closeDeleteConfirmModal = () => {
		setShowDeleteConfirmModal(false);
	}

    return (
		<div className="modal-wrapper">
			<div className="UndoConfirmModal modal">
				<p>Are you sure you want to delete {label} </p>
				<div className="bottom-container">
					<button className="button cancel-button" onClick={closeDeleteConfirmModal}>Cancel</button>
					<button className="button modal-button" onClick={handleDeleteConfirm}>Ok</button>			
				</div>
			</div>
		</div>
    )
}

export default ScreenPage
